import { BadgeCheck, Recycle, Leaf } from "lucide-react";
import { CERTIFICATIONS, IMAGES, PACKAGING_CERTIFICATIONS } from "@/lib/company";
import { Link } from "react-router-dom";

const groups = [
  {
    title: "Manufacturing & Quality Standards",
    items: ["WHO-GMP Certified", "ISO 22000:2018", "GMP Certified", "HACCP Compliant", "FDCA (Gujarat)", "IAF Accredited"],
  },
  {
    title: "Indian Regulatory & Trade",
    items: ["FSSAI Certified", "Pharmexcil Member", "Make in India", "SHEFEXIL Member", "Star Export House"],
  },
  {
    title: "International & Organic",
    items: ["FDA Registered", "USDA Organic", "India Organic", "Halal Certified", "Kosher Certified", "Control Union", "OneCert Organic"],
  },
];

export default function Certifications() {
  return (
    <div data-testid="certifications-page">
      <section className="relative min-h-[45vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.warehouse} alt="Warehouse" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-24 w-full">
          <span className="inline-block px-4 py-1.5 bg-brand-gold text-brand-green-dark text-[11px] font-bold uppercase tracking-[0.25em] rounded-sm mb-5">Certifications</span>
          <h1 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-tight max-w-4xl">
            The Certifications Our<br /><span className="text-brand-gold">Manufacturing and Processing Partners</span> Hold
          </h1>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 space-y-16">
          {groups.map((g, gi) => (
            <div key={gi}>
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">{`Group ${gi + 1}`}</span>
              <h2 className="mt-2 font-display font-bold text-brand-charcoal text-2xl md:text-3xl gold-underline">{g.title}</h2>
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.items.map((c) => (
                  <div key={c} data-testid={`cert-${c.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-3 p-5 bg-brand-cream border border-neutral-200 rounded-md hover:border-brand-gold transition-colors">
                    <BadgeCheck className="w-6 h-6 text-brand-green shrink-0" />
                    <span className="font-semibold text-brand-charcoal">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-[#F7F5EF] border-t border-neutral-200" data-testid="packaging-certs-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Eco-Packaging Category</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline">
              Every Cup, Straw &amp; Lid — Independently Verified
            </h2>
            <p className="mt-5 text-brand-charcoal/75 leading-relaxed">
              Our compostable food-service portfolio is audited across four continents. From forest fibre to end-of-life composting, every claim is backed by an accredited certificate — issued to our manufacturing partners and shareable on request.
            </p>
          </div>

          <div className="mt-14 space-y-14">
            {PACKAGING_CERTIFICATIONS.map((group, gi) => (
              <div key={group.title} data-testid={`packaging-cert-group-${gi}`}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-md bg-brand-green flex items-center justify-center shrink-0">
                    {gi === 0 ? (
                      <Recycle className="w-5 h-5 text-brand-gold" />
                    ) : gi === 1 ? (
                      <Leaf className="w-5 h-5 text-brand-gold" />
                    ) : (
                      <BadgeCheck className="w-5 h-5 text-brand-gold" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-brand-charcoal text-xl md:text-2xl">{group.title}</h3>
                    <p className="mt-1 text-sm text-brand-charcoal/70">{group.subtitle}</p>
                  </div>
                </div>
                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.items.map((it) => (
                    <div
                      key={it.full}
                      data-testid={`pkg-cert-${it.short.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group relative bg-white border border-neutral-200 rounded-md p-5 hover:border-brand-gold hover:shadow-md transition"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">{it.short}</div>
                          <div className="mt-1 font-display font-bold text-brand-charcoal text-base leading-snug">{it.full}</div>
                        </div>
                        <BadgeCheck className="w-5 h-5 text-brand-green shrink-0 mt-0.5" />
                      </div>
                      <p className="mt-3 text-xs text-brand-charcoal/70 leading-relaxed">{it.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-lg bg-white border border-neutral-200 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Full Compliance Dossier</div>
              <div className="mt-2 font-display font-bold text-brand-charcoal text-xl">
                Certificate copies available under NDA on active RFQs.
              </div>
            </div>
            <Link to="/contact" data-testid="pkg-cert-request" className="btn-green shrink-0">Request Certificate Pack</Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-green text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h3 className="font-display font-bold text-2xl md:text-3xl">Need copies of any specific certification?</h3>
          <p className="mt-4 text-white/80">We share manufacturer-specific certificates on request, matched to your order.</p>
          <Link to="/contact" data-testid="cert-cta" className="mt-8 btn-gold inline-flex">Request Certificates</Link>
        </div>
      </section>
    </div>
  );
}
